import { toast } from '@/components/toast';
import { studioDomain } from '@/environment';
import { HttpStatus } from '@/errors/http-status';
import { getOrCreateClientKey, patchClientKey } from '@/services/api';
import { logger } from '@/services/logger';
import { IAgentKey } from '@d-id/types';
import { useMutation, useQuery, useQueryClient } from 'react-query';

export const getOrCreateAgentClientKey = (): Promise<IAgentKey[]> => {
    const req = {
        use_case: 'agents',
        allowed_domains: [studioDomain],
    } as IAgentKey;
    return getOrCreateClientKey(req);
};

export const GET_CLIENT_KEY = 'GET_CLIENT_KEY';
export function useClientKey({ isAnonymous }: { isAnonymous: boolean | undefined }) {
    return useQuery<IAgentKey>(
        GET_CLIENT_KEY,
        async () => {
            const keys = await getOrCreateAgentClientKey();
            return keys[0];
        },
        {
            enabled: !isAnonymous,
            retry: 5,
            retryDelay: 1000,
            onError: async (error: any) => {
                if (!handleMaxAllowedDomainsError(error)) {
                    logger.error('Could not get client key', { error });
                    toast.error({ title: `Could not get client key`, toastId: `agent-client-key-get-error` });
                }
            },
        }
    );
}

export function usePatchClientKey() {
    const queryClient = useQueryClient();

    return useMutation(patchClientKey, {
        onError: async (error: any) => {
            if (!handleMaxAllowedDomainsError(error)) {
                logger.error('Could not update client key', { error });
                toast.error({ title: `Could not update client key`, toastId: `agent-client-key-patch-error` });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries(GET_CLIENT_KEY);
        },
    });
}

function handleMaxAllowedDomainsError(error: any) {
    if (error.response?.status === HttpStatus.MAX_ALLOWED_DOMAINS_LIMIT_REACHED) {
        logger.error(error.response.data.description);
        toast.error({
            title: error.response.data.description,
            toastId: `agent-client-key-patch-error`,
        });
        return true;
    }
    return false;
}
